import { useState } from 'react';
import { requestLink, order, type OrderInfo } from '../account';
import { useAccount } from '../account/context';
import { usePricing } from '../pricing';

/** The account modals (sign-in, order) + the profile overlay (email + scan history),
 *  driven by the shared account modal state. Rendered once, near the app root (#4). */
export default function AccountModals(): JSX.Element | null {
  const { account, modal, setModal } = useAccount();
  const pricing = usePricing();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<{ devLink?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [cycle, setCycle] = useState<'monthly' | 'annual'>('monthly');

  function close(): void { setModal('none'); setSent(null); setOrderInfo(null); setErr(null); }

  async function submitEmail(): Promise<void> {
    setBusy(true); setErr(null);
    try { setSent(await requestLink(email)); }
    catch (e) { setErr(e instanceof Error ? e.message : 'failed'); }
    finally { setBusy(false); }
  }
  async function placeOrder(): Promise<void> {
    setBusy(true); setErr(null);
    try { setOrderInfo(await order(cycle)); }
    catch (e) { setErr(e instanceof Error ? e.message : 'failed'); }
    finally { setBusy(false); }
  }

  if (modal === 'none') return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm px-6 py-10 overflow-y-auto" onClick={close}>
      <div className="w-full max-w-md rounded-xl border border-cyan/40 bg-warm-dark p-6" onClick={(e) => e.stopPropagation()}>

        {modal === 'signin' && (
          <>
            <p className="font-serif text-xl mb-1">Sign in</p>
            <p className="text-sm text-text-secondary mb-4">Enter your email. I send you a one-time sign-in link, click it and you're in. No password. If you have Pro access, it applies once you sign in.</p>
            {sent ? (
              <div className="text-sm text-text-secondary space-y-2">
                <p className="text-cyan">Check your inbox. A sign-in link is on its way to {email}.</p>
                {sent.devLink && <p className="break-all">Dev link: <a href={sent.devLink} className="text-cyan underline">{sent.devLink}</a></p>}
              </div>
            ) : (
              <>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                  className="w-full bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded-lg px-4 py-3 text-text-primary placeholder:text-text-secondary outline-none font-mono text-sm mb-3" />
                {err && <p className="text-sm text-[#E0574A] mb-2">{err}</p>}
                <button disabled={busy || !email} onClick={() => void submitEmail()} className="w-full bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2.5 text-sm disabled:opacity-50 hover:opacity-90 transition">
                  {busy ? 'Sending…' : 'Email me a link'}
                </button>
              </>
            )}
          </>
        )}

        {modal === 'order' && (
          orderInfo ? (
            <div className="text-sm text-text-secondary space-y-3">
              <p className="font-serif text-xl text-text-primary mb-1">Thanks, your order is in</p>
              <p>I will email your invoice to <span className="text-cyan">{orderInfo.email}</span> for PageCoder Pro (<span className="font-mono">€{orderInfo.amountEur}</span> / {orderInfo.cycle}). Pay it by bank transfer, and your Pro access is activated as soon as I have confirmed the payment.</p>
              <button onClick={close} className="mt-1 font-mono text-sm text-cyan hover:underline">Done</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <p className="font-serif text-xl">Go Pro, €{pricing.monthlyEur}/mo</p>
                {pricing.promo.active && pricing.promo.badge && (
                  <span className="font-mono text-xs uppercase tracking-wide text-warm-dark bg-cyan rounded px-1.5 py-0.5">{pricing.promo.badge}</span>
                )}
              </div>
              <p className="text-sm text-text-secondary mb-4">Everything: full scans, the Fix it plan, FAQ drafting, history and trends. No trial, the free test is your trial.</p>
              <div className="flex gap-2 mb-4">
                {(['monthly', 'annual'] as const).map((c) => (
                  <button key={c} onClick={() => setCycle(c)} className={`flex-1 rounded-lg border px-3 py-2 text-sm ${cycle === c ? 'border-cyan text-cyan' : 'border-warm-dark-2 text-text-secondary'}`}>
                    {c === 'monthly' ? `€${pricing.monthlyEur} / month` : `€${pricing.annualEur} / year`}
                  </button>
                ))}
              </div>
              <p className="text-sm text-text-secondary/80 mb-3">I will email a proper invoice to {account?.email} with payment details. Pro activates once payment is confirmed.</p>
              {err && <p className="text-sm text-[#E0574A] mb-2">{err}</p>}
              <button disabled={busy} onClick={() => void placeOrder()} className="w-full bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2.5 text-sm disabled:opacity-50 hover:opacity-90 transition">
                {busy ? 'Ordering…' : 'Order Pro, email me the invoice'}
              </button>
            </>
          )
        )}
      </div>
    </div>
  );
}
